import React, { useCallback, useEffect, useState } from "react";
import { FaCheck, FaEye, FaEyeSlash, FaEnvelope, FaMicrosoft, FaMobileAlt, FaSave, FaShieldAlt, FaVial } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import "./MfaSettings.css";

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

const EMPTY = {
  enabled:           false,
  method:            "email",
  otpExpiryMinutes:  10,
  emailProvider:     "smtp",
  smtpHost:          "",
  smtpPort:          587,
  smtpSecure:        false,
  smtpUser:          "",
  smtpPass:          "",
  smtpFrom:          "",
  graphTenantId:     "",
  graphClientId:     "",
  graphClientSecret: "",
  graphFromAddress:  "",
  twilioAccountSid:  "",
  twilioAuthToken:   "",
  twilioFromNumber:  "",
};

export default function MfaSettings() {
  const { token } = useAuth();

  const [form, setForm]                         = useState(EMPTY);
  const [hasSmtpPass, setHasSmtpPass]           = useState(false);
  const [hasGraphSecret, setHasGraphSecret]     = useState(false);
  const [hasTwilioAuth, setHasTwilioAuth]       = useState(false);
  const [showSmtpPass, setShowSmtpPass]         = useState(false);
  const [showGraphSecret, setShowGraphSecret]   = useState(false);
  const [showTwilioAuth, setShowTwilioAuth]     = useState(false);
  const [saving, setSaving]                     = useState(false);
  const [banner, setBanner]                     = useState(null); // { type, msg }
  const [testEmailAddr, setTestEmailAddr]       = useState("");
  const [testSmsNumber, setTestSmsNumber]       = useState("");
  const [testMsg, setTestMsg]                   = useState(null); // { type, msg }
  const [testing, setTesting]                   = useState(false);

  const authHeaders = useCallback(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }), [token]);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/admin/mfa-settings`, { headers: authHeaders() });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Load failed");
      const s = j.settings;
      if (s) {
        setForm({
          enabled:           s.enabled,
          method:            s.method           || "email",
          otpExpiryMinutes:  s.otpExpiryMinutes || 10,
          emailProvider:     s.emailProvider    || "smtp",
          smtpHost:          s.smtpHost         || "",
          smtpPort:          s.smtpPort         || 587,
          smtpSecure:        !!s.smtpSecure,
          smtpUser:          s.smtpUser         || "",
          smtpPass:          "",
          smtpFrom:          s.smtpFrom         || "",
          graphTenantId:     s.graphTenantId    || "",
          graphClientId:     s.graphClientId    || "",
          graphClientSecret: "",
          graphFromAddress:  s.graphFromAddress || "",
          twilioAccountSid:  s.twilioAccountSid || "",
          twilioAuthToken:   "",
          twilioFromNumber:  s.twilioFromNumber || "",
        });
        setHasSmtpPass(s.hasSmtpPass);
        setHasGraphSecret(s.hasGraphSecret);
        setHasTwilioAuth(s.hasTwilioAuth);
      }
    } catch (e) {
      setBanner({ type: "error", msg: e.message });
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setBanner(null); };

  const handleSave = async (e) => {
    e.preventDefault();
    setBanner(null);
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE}/api/admin/mfa-settings`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Save failed");
      setBanner({ type: "success", msg: "MFA settings saved." });
      await load();
    } catch (e) {
      setBanner({ type: "error", msg: e.message });
    } finally {
      setSaving(false);
    }
  };

  const testEmail = async () => {
    setTesting(true); setTestMsg(null);
    try {
      const r = await fetch(`${API_BASE}/api/admin/mfa-settings/test-email`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ toAddress: testEmailAddr }),
      });
      const j = await r.json();
      setTestMsg(r.ok ? { type: "success", msg: j.message } : { type: "error", msg: j.error });
    } catch (e) {
      setTestMsg({ type: "error", msg: e.message });
    } finally {
      setTesting(false);
    }
  };

  const testSms = async () => {
    setTesting(true); setTestMsg(null);
    try {
      const r = await fetch(`${API_BASE}/api/admin/mfa-settings/test-sms`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ toNumber: testSmsNumber }),
      });
      const j = await r.json();
      setTestMsg(r.ok ? { type: "success", msg: j.message } : { type: "error", msg: j.error });
    } catch (e) {
      setTestMsg({ type: "error", msg: e.message });
    } finally {
      setTesting(false);
    }
  };

  const needsEmail = form.method === "email" || form.method === "both";
  const needsSms   = form.method === "sms"   || form.method === "both";

  return (
    <div className="mfa-page">

      {/* ── Header ── */}
      <div className="mfa-header">
        <div className="mfa-header-icon"><FaShieldAlt /></div>
        <div>
          <h2 className="mfa-header-title">Multi-Factor Authentication</h2>
          <p className="mfa-header-desc">
            Require a one-time code on every login for non-SSO users.
            Configure the delivery channel and credentials below.
          </p>
        </div>
      </div>

      {/* ── Guide ── */}
      <div className="mfa-guide">
        <p className="mfa-guide-title">Before enabling MFA</p>
        <ol className="mfa-guide-list">
          <li>Choose a delivery method: <strong>Email</strong>, <strong>SMS</strong>, or both.</li>
          <li>For email, configure <em>SMTP</em> credentials or an <em>Azure App Registration</em> with the <strong>Mail.Send</strong> permission.</li>
          <li>For SMS, provide a <strong>Twilio</strong> Account SID, Auth Token, and From number.</li>
          <li>Ensure users have a <strong>phone number</strong> set in User Management if SMS delivery is required.</li>
          <li>Use <strong>Test email / Test SMS</strong> to verify delivery before enabling.</li>
        </ol>
      </div>

      {banner && (
        <div className={`mfa-banner mfa-banner--${banner.type}`}>
          {banner.type === "success" && <FaCheck style={{ marginRight: 7 }} />}
          {banner.msg}
        </div>
      )}

      <form className="mfa-form" onSubmit={handleSave}>

        {/* ── Enable toggle ── */}
        <div className="mfa-toggle-row">
          <label className="mfa-toggle-label" htmlFor="mfa-enabled">
            <span className="mfa-toggle-text">
              <span className="mfa-toggle-title">Enable MFA</span>
              <span className="mfa-toggle-sub">
                When on, all non-SSO users must complete a code challenge on every login.
              </span>
            </span>
            <span className={`mfa-toggle-pill ${form.enabled ? "on" : "off"}`}>
              <input id="mfa-enabled" type="checkbox" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} />
              <span className="mfa-pill-thumb" />
            </span>
          </label>
        </div>

        {/* ── General ── */}
        <div className="mfa-section">
          <p className="mfa-section-title">General</p>
          <div className="mfa-fields">

            <label className="mfa-field">
              <span className="mfa-field-label">Delivery method</span>
              <div className="mfa-radio-group">
                {[["email", "Email only"], ["sms", "SMS only"], ["both", "Email + SMS"]].map(([v, l]) => (
                  <label key={v} className="mfa-radio-option">
                    <input type="radio" name="mfa-method" value={v} checked={form.method === v} onChange={() => set("method", v)} />
                    {l}
                  </label>
                ))}
              </div>
            </label>

            <label className="mfa-field" style={{ maxWidth: 160 }}>
              <span className="mfa-field-label">Code expiry (minutes)</span>
              <input type="number" min={1} max={60} className="mfa-input" value={form.otpExpiryMinutes} onChange={(e) => set("otpExpiryMinutes", Number(e.target.value))} />
            </label>

          </div>
        </div>

        {/* ── Email sender ── */}
        {needsEmail && (
          <div className="mfa-section">
            <p className="mfa-section-title">Email sender</p>
            <div className="mfa-fields">

              <label className="mfa-field">
                <span className="mfa-field-label">Email provider</span>
                <div className="mfa-segment-control">
                  <button type="button" className={`mfa-segment-btn${form.emailProvider === "smtp" ? " active" : ""}`} onClick={() => set("emailProvider", "smtp")}>
                    <FaEnvelope /> SMTP
                  </button>
                  <button type="button" className={`mfa-segment-btn${form.emailProvider === "graph" ? " active" : ""}`} onClick={() => set("emailProvider", "graph")}>
                    <FaMicrosoft /> Microsoft Graph
                  </button>
                </div>
              </label>

              {/* SMTP */}
              {form.emailProvider === "smtp" && (<>
                <div className="mfa-fields-grid">
                  <label className="mfa-field">
                    <span className="mfa-field-label">SMTP host <span className="mfa-required">*</span></span>
                    <input className="mfa-input" value={form.smtpHost} onChange={(e) => set("smtpHost", e.target.value)} placeholder="smtp.example.com" />
                  </label>
                  <label className="mfa-field">
                    <span className="mfa-field-label">Port</span>
                    <input type="number" className="mfa-input" value={form.smtpPort} onChange={(e) => set("smtpPort", Number(e.target.value))} placeholder="587" />
                  </label>
                </div>

                <div className="mfa-toggle-row mfa-toggle-row--inline">
                  <label className="mfa-toggle-label" htmlFor="mfa-tls">
                    <span className="mfa-toggle-text">
                      <span className="mfa-toggle-title">TLS / SSL</span>
                      <span className="mfa-toggle-sub">Enable for port 465. Leave off for STARTTLS on port 587.</span>
                    </span>
                    <span className={`mfa-toggle-pill ${form.smtpSecure ? "on" : "off"}`}>
                      <input id="mfa-tls" type="checkbox" checked={form.smtpSecure} onChange={(e) => set("smtpSecure", e.target.checked)} />
                      <span className="mfa-pill-thumb" />
                    </span>
                  </label>
                </div>

                <div className="mfa-fields-grid">
                  <label className="mfa-field">
                    <span className="mfa-field-label">SMTP username</span>
                    <input className="mfa-input" value={form.smtpUser} onChange={(e) => set("smtpUser", e.target.value)} placeholder="noreply@example.com" autoComplete="off" />
                  </label>
                  <label className="mfa-field">
                    <span className="mfa-field-label">
                      SMTP password {hasSmtpPass && !form.smtpPass && <span className="mfa-stored">(stored — leave blank to keep)</span>}
                    </span>
                    <div className="mfa-secret-wrap">
                      <input type={showSmtpPass ? "text" : "password"} className="mfa-input mfa-input--secret" value={form.smtpPass} onChange={(e) => set("smtpPass", e.target.value)} placeholder={hasSmtpPass ? "Leave blank to keep existing" : "Enter password"} autoComplete="new-password" />
                      <button type="button" className="mfa-secret-toggle" onClick={() => setShowSmtpPass((p) => !p)} aria-label={showSmtpPass ? "Hide" : "Show"}>
                        {showSmtpPass ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </label>
                </div>

                <label className="mfa-field">
                  <span className="mfa-field-label">From address</span>
                  <input className="mfa-input" value={form.smtpFrom} onChange={(e) => set("smtpFrom", e.target.value)} placeholder="PhishMatrix AI <noreply@example.com>" />
                </label>
              </>)}

              {/* Microsoft Graph */}
              {form.emailProvider === "graph" && (<>
                <div className="mfa-fields-grid">
                  <label className="mfa-field">
                    <span className="mfa-field-label">Tenant ID <span className="mfa-required">*</span></span>
                    <input className="mfa-input" value={form.graphTenantId} onChange={(e) => set("graphTenantId", e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" autoComplete="off" />
                  </label>
                  <label className="mfa-field">
                    <span className="mfa-field-label">Client ID <span className="mfa-required">*</span></span>
                    <input className="mfa-input" value={form.graphClientId} onChange={(e) => set("graphClientId", e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" autoComplete="off" />
                  </label>
                </div>

                <label className="mfa-field">
                  <span className="mfa-field-label">
                    Client secret <span className="mfa-required">*</span>
                    {hasGraphSecret && !form.graphClientSecret && <span className="mfa-stored">(stored — leave blank to keep)</span>}
                  </span>
                  <div className="mfa-secret-wrap">
                    <input type={showGraphSecret ? "text" : "password"} className="mfa-input mfa-input--secret" value={form.graphClientSecret} onChange={(e) => set("graphClientSecret", e.target.value)} placeholder={hasGraphSecret ? "Leave blank to keep existing" : "Enter client secret"} autoComplete="new-password" />
                    <button type="button" className="mfa-secret-toggle" onClick={() => setShowGraphSecret((p) => !p)} aria-label={showGraphSecret ? "Hide" : "Show"}>
                      {showGraphSecret ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </label>

                <label className="mfa-field">
                  <span className="mfa-field-label">From address <span className="mfa-hint">(shared mailbox or user UPN)</span></span>
                  <input className="mfa-input" value={form.graphFromAddress} onChange={(e) => set("graphFromAddress", e.target.value)} placeholder="noreply@yourdomain.com" />
                  <span className="mfa-field-help">App registration requires the <strong>Mail.Send</strong> application permission with admin consent.</span>
                </label>
              </>)}

              {/* Test send */}
              <div className="mfa-test-row">
                <input className="mfa-input" style={{ flex: 1 }} placeholder="Send test code to (email address)" value={testEmailAddr} onChange={(e) => setTestEmailAddr(e.target.value)} />
                <button type="button" className="mfa-btn mfa-btn--test" onClick={testEmail} disabled={testing || !testEmailAddr}>
                  <FaVial /> Test email
                </button>
              </div>

            </div>
          </div>
        )}

        {/* ── SMS / Twilio ── */}
        {needsSms && (
          <div className="mfa-section">
            <p className="mfa-section-title">SMS provider (Twilio)</p>
            <div className="mfa-fields">

              <div className="mfa-fields-grid">
                <label className="mfa-field">
                  <span className="mfa-field-label">Account SID <span className="mfa-required">*</span></span>
                  <input className="mfa-input" value={form.twilioAccountSid} onChange={(e) => set("twilioAccountSid", e.target.value)} placeholder="ACxxxxxxxxxxxxxxxx" autoComplete="off" />
                </label>
                <label className="mfa-field">
                  <span className="mfa-field-label">
                    Auth token <span className="mfa-required">*</span>
                    {hasTwilioAuth && !form.twilioAuthToken && <span className="mfa-stored">(stored — leave blank to keep)</span>}
                  </span>
                  <div className="mfa-secret-wrap">
                    <input type={showTwilioAuth ? "text" : "password"} className="mfa-input mfa-input--secret" value={form.twilioAuthToken} onChange={(e) => set("twilioAuthToken", e.target.value)} placeholder={hasTwilioAuth ? "Leave blank to keep existing" : "Enter auth token"} autoComplete="new-password" />
                    <button type="button" className="mfa-secret-toggle" onClick={() => setShowTwilioAuth((p) => !p)} aria-label={showTwilioAuth ? "Hide" : "Show"}>
                      {showTwilioAuth ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </label>
              </div>

              <label className="mfa-field" style={{ maxWidth: 220 }}>
                <span className="mfa-field-label">From number <span className="mfa-required">*</span></span>
                <input className="mfa-input" value={form.twilioFromNumber} onChange={(e) => set("twilioFromNumber", e.target.value)} placeholder="+15551234567" />
                <span className="mfa-field-help">Users must have a phone number set in User Management to receive SMS codes.</span>
              </label>

              <div className="mfa-test-row">
                <input className="mfa-input" style={{ flex: 1 }} placeholder="Send test code to (+1xxxxxxxxxx)" value={testSmsNumber} onChange={(e) => setTestSmsNumber(e.target.value)} />
                <button type="button" className="mfa-btn mfa-btn--test" onClick={testSms} disabled={testing || !testSmsNumber}>
                  <FaMobileAlt /> Test SMS
                </button>
              </div>

            </div>
          </div>
        )}

        {testMsg && (
          <div className={`mfa-banner mfa-banner--${testMsg.type}`}>
            {testMsg.type === "success" && <FaCheck style={{ marginRight: 7 }} />}
            {testMsg.msg}
          </div>
        )}

        {/* ── Actions ── */}
        <div className="mfa-actions">
          <button className="mfa-btn mfa-btn--save" type="submit" disabled={saving}>
            <FaSave />
            {saving ? "Saving…" : "Save Configuration"}
          </button>
        </div>

      </form>
    </div>
  );
}
